/**
 * Contractor management API client.
 *
 * One place that knows the contractor endpoints, so a page never hand-rolls a
 * URL. Follows the `API` convention from App.js (which already includes /api).
 */
import axios from 'axios';
import { API } from '../App';

const auth = () => ({
  headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
});

const base = () => `${API}/contractors`;

/* ---------------------------------------------------------------- companies */

export async function fetchContractors({ signal } = {}) {
  const { data } = await axios.get(`${base()}/companies`, { ...auth(), signal });
  return data || {};
}

export async function createContractor(body) {
  const { data } = await axios.post(`${base()}/companies`, body, auth());
  return data || {};
}

export async function updateContractor(id, body) {
  const { data } = await axios.put(`${base()}/companies/${id}`, body, auth());
  return data || {};
}

export async function deleteContractor(id) {
  const { data } = await axios.delete(`${base()}/companies/${id}`, auth());
  return data || {};
}

/* ------------------------------------------------------------------ workers */

export async function fetchWorkers({ siteId } = {}, { signal } = {}) {
  const params = {};
  if (siteId) params.site_id = siteId;
  const { data } = await axios.get(`${base()}/workers`, { ...auth(), params, signal });
  return data || {};
}

export async function createWorker(body) {
  const { data } = await axios.post(`${base()}/workers`, body, auth());
  return data || {};
}

export async function updateWorker(id, body) {
  const { data } = await axios.put(`${base()}/workers/${id}`, body, auth());
  return data || {};
}

/* -------------------------------------------------------------------- types */

/** Requirement templates. The server seeds sensible defaults on first read, so
 *  a tenant that configured nothing still gets a usable set. */
export async function fetchContractorTypes({ signal } = {}) {
  const { data } = await axios.get(`${base()}/types`, { ...auth(), signal });
  return data || {};
}

export async function createContractorType(body) {
  const { data } = await axios.post(`${base()}/types`, body, auth());
  return data || {};
}

export async function updateContractorType(id, body) {
  const { data } = await axios.put(`${base()}/types/${id}`, body, auth());
  return data || {};
}

export async function deleteContractorType(id) {
  const { data } = await axios.delete(`${base()}/types/${id}`, auth());
  return data || {};
}

/* -------------------------------------------------------------- assignments */

/**
 * Contractor-to-site engagements. With siteId and workType this is the
 * context-aware contractor list: the firms actually engaged here for this kind
 * of work, rather than every contractor in the tenant.
 */
export async function fetchAssignments(
  { contractorId, siteId, workType } = {}, { signal } = {},
) {
  const params = {};
  if (contractorId) params.contractor_company_id = contractorId;
  if (siteId) params.site_id = siteId;
  if (workType) params.work_type = workType;
  const { data } = await axios.get(`${base()}/assignments`, { ...auth(), params, signal });
  return data || {};
}

export async function saveAssignment(body) {
  const { data } = await axios.post(`${base()}/assignments`, body, auth());
  return data || {};
}

export async function deleteAssignment(id) {
  const { data } = await axios.delete(`${base()}/assignments/${id}`, auth());
  return data || {};
}

/* -------------------------------------------------------------- eligibility */

/**
 * Why a worker may or may not do controlled work here. Returns
 * { eligible, blockers[], expiring_soon[], satisfied[] } — never a bare
 * boolean, so the UI can always say what to fix.
 */
export async function fetchWorkerEligibility(
  workerId, { siteId, workType } = {}, { signal } = {},
) {
  const params = {};
  if (siteId) params.site_id = siteId;
  if (workType) params.work_type = workType;
  const { data } = await axios.get(
    `${base()}/workers/${workerId}/eligibility`, { ...auth(), params, signal },
  );
  return data || {};
}

/** Many workers in one round trip — a permit can carry a hundred. */
export async function fetchBulkEligibility(
  { workerIds, siteId, workType } = {}, { signal } = {},
) {
  const { data } = await axios.post(`${base()}/workers/eligibility`, {
    worker_ids: workerIds || [], site_id: siteId, work_type: workType,
  }, { ...auth(), signal });
  return data || {};
}

export async function fetchContractorEligibility(
  contractorId, { siteId, workType } = {}, { signal } = {},
) {
  const params = {};
  if (siteId) params.site_id = siteId;
  if (workType) params.work_type = workType;
  const { data } = await axios.get(
    `${base()}/companies/${contractorId}/eligibility`, { ...auth(), params, signal },
  );
  return data || {};
}

/** Progressive completion per section — the same rules the gate uses. */
export async function fetchContractorCompliance(
  contractorId, { siteId } = {}, { signal } = {},
) {
  const params = {};
  if (siteId) params.site_id = siteId;
  const { data } = await axios.get(
    `${base()}/companies/${contractorId}/compliance`, { ...auth(), params, signal },
  );
  return data || {};
}

/** Eligibility of the workers on one permit, plus the enforcement mode. */
export async function fetchPermitWorkerEligibility(permitId, { signal } = {}) {
  const { data } = await axios.get(
    `${API}/permits/${permitId}/worker-eligibility`, { ...auth(), signal },
  );
  return data || {};
}

/* ------------------------------------------------------- documents (files) */

/**
 * Contractor documents are ordinary platform uploads — there is no
 * contractor_documents table. They are addressed by module + reference_id, and
 * the DOCUMENT TYPE travels in `tags`.
 *
 * Note it is NOT `category`: that field is derived from the MIME type
 * (document / image / spreadsheet), so it describes the file, not which
 * requirement it satisfies. Eligibility reads the tag.
 */
export async function fetchContractorDocuments(contractorId, { signal } = {}) {
  const { data } = await axios.get(`${API}/files`, {
    ...auth(),
    params: { module: 'contractor', reference_id: contractorId, page_size: 200 },
    signal,
  });
  return data || {};
}

/** Upload one document against a contractor, tagged with its type. */
export async function uploadContractorDocument(
  contractorId, file, { documentType, description } = {},
) {
  const body = new FormData();
  body.append('file', file);
  body.append('module', 'contractor');
  body.append('reference_id', contractorId);
  // The server stores `tags` as tags.split(','), untrimmed — so send the bare
  // key with no spaces or the stored tag will not match the eligibility lookup.
  if (documentType) body.append('tags', String(documentType).trim());
  if (description) body.append('description', description);
  const { data } = await axios.post(`${API}/files/upload`, body, {
    ...auth(),
    headers: { ...auth().headers, 'Content-Type': 'multipart/form-data' },
  });
  return data || {};
}

/** A short-lived download URL for one document. */
export async function contractorDocumentDownloadUrl(fileId) {
  const { data } = await axios.get(`${API}/files/${fileId}/download`, auth());
  return data?.download_url || data?.url || null;
}

export async function deleteContractorDocument(fileId) {
  const { data } = await axios.delete(`${API}/files/${fileId}`, auth());
  return data || {};
}
